import {
  AllowedMaterialSpec,
  MaterialComponentCategory,
  SiteMaterialDetail,
  SiteMaterialList,
  useApiTradeV1SiteMaterialsRetrieve,
} from 'api';
import { MaterialSupportedComponentList } from 'features';
import {
  AllowedMaterialSpecAutocomplete,
  BooleanSelectField,
  BuyerMaterialSpecificationsAutocomplete,
  MaterialGradeAutocomplete,
  MaterialOriginAutocomplete,
  SiteMaterialWithProductAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  useFormContext,
  useWatch,
} from 'form';
import { useContext, useEffect } from 'react';
import { Box, Button, Divider, Grid, Typography } from 'ui';

import { MaterialDirtLevel } from '~/material';

import { SalesOrderFormContext } from '../../contexts/SalesOrderFormContext';
import { BaseSalesOrderFormValues } from '../../types';
import { isMaterialProductSyncedWithMaterialProps } from './isMaterialProductSyncedWithMaterialProps';
import { updateMaterialProperties } from './updateMaterialProperties';

export const BuyerMaterialDetailsSection = () => {
  const { setValue, watch, getValues } = useFormContext<BaseSalesOrderFormValues>();
  const { purchaser_id: purchaserId } = useWatch<BaseSalesOrderFormValues>();

  const { defaultValues, variant } = useContext(SalesOrderFormContext);
  const siteMaterialId = watch('site_material_id') || '';
  const sellerId = watch('seller_id');
  const materialId = watch('material_id');
  const materialColourId = watch('material_colour_id');
  const { data: siteMaterialDetail } = useApiTradeV1SiteMaterialsRetrieve(siteMaterialId?.toString());
  const values = getValues();
  const onSyncMaterial = (siteMaterialDetail: SiteMaterialDetail | SiteMaterialList | undefined) => {
    if (!siteMaterialDetail) {
      return;
    }
    updateMaterialProperties(siteMaterialDetail, setValue);
  };

  const onAllowedMaterialSpecChange = (value: AllowedMaterialSpec) => {
    setValue('material_id', value.material_id);
    setValue('material_colour_id', value.colour_id ?? undefined);
  };

  useEffect(() => {
    if (!sellerId && siteMaterialDetail?.site?.trade_company?.id) {
      setValue('seller_id', siteMaterialDetail?.site?.trade_company?.id);
    }
  }, [sellerId, siteMaterialDetail, setValue]);

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold">
        Material
      </Typography>
      <Grid container spacing={2} marginBottom={2} marginTop={0}>
        <Grid item xs={12} md={8}>
          <BuyerMaterialSpecificationsAutocomplete
            name="buyer_material_spec_id"
            tradeCompanyId={purchaserId}
            infoTooltip="The buyer material specification which this sales order is for."
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TradeCompanyAutocomplete
            name="seller_id"
            label="Seller"
            onlySellers={true}
            infoTooltip="Company that produced the material Safi is purchasing."
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <SiteMaterialWithProductAutocomplete
            label="Site material"
            name="site_material_id"
            tradeCompanyId={sellerId}
            onSelectOption={(siteMaterial) => onSyncMaterial(siteMaterial)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            disabled={isMaterialProductSyncedWithMaterialProps(values, siteMaterialDetail)}
            onClick={() => onSyncMaterial(siteMaterialDetail)}
          >
            Sync material properties
          </Button>
        </Grid>
        <Grid item xs={12} md={4}>
          <AllowedMaterialSpecAutocomplete
            name="material_spec_id"
            label="Material"
            required
            defaultValue={defaultValues?.material_spec_id}
            infoTooltip="If the right spec is not present, request the Quality team to add it."
            onSelectOption={onAllowedMaterialSpecChange}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MaterialGradeAutocomplete
            label="Grade"
            name="grade_id"
            required
            infoTooltip="Material grade. Attach a Quality Report if agreed as per that specification."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MaterialOriginAutocomplete label="Origin" name="origin_id" infoTooltip="Provenance of the material." />
        </Grid>
        <Grid item xs={12} md={4}>
          <MaterialDirtLevel
            label="Dirt Level"
            name="dirt_level"
            infoTooltip="The amount of dirt a buyer will accept. If blank this is assumed based on the grade."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <BooleanSelectField
            label="Food grade"
            name="is_food_grade"
            infoTooltip="Confirmation this plastic is safe to reprocess for contact with food and beverages."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <BooleanSelectField
            label="Contains caps and labels"
            name="has_caps_and_labels"
            withNotAvailable
            infoTooltip="Only used on PET Bales. Select 'No' if the buyer or supplier of this material specifically asked for 'no caps or labels'."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Material ewc code override"
            name="material_ewc_code_override"
            type="text"
            fullWidth
            required={false}
            infoTooltip="Overrides the EWC code on Safi documents. To be used only if the purchaser requires different EWC code for customs purposes."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Material customs name override"
            name="material_customs_name_override"
            type="text"
            fullWidth
            required={false}
            infoTooltip="Overrides the material name on Safi documents. To be used only if the purchaser requires different material name for customs purposes."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Basel code override"
            name="basel_code_override"
            inputProps={{
              maxLength: 8,
            }}
            infoTooltip="Overrides basel code on Safi documents. To be used only if the purchaser requires different basel code for customs purposes."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          {variant === 'update' ? (
            <TextField
              label="Material hs code override"
              name="material_hs_code_override"
              type="text"
              fullWidth
              required={false}
              infoTooltip="Overrides the material name on Safi documents. To be used only if different basel code is required for customs purposes."
            />
          ) : null}
        </Grid>
      </Grid>
      <MaterialSupportedComponentList
        name="seller_material_component_values"
        materialId={materialId}
        materialColourId={materialColourId}
        title="Seller material components"
        buttonText="Add seller material component"
        category={MaterialComponentCategory.MATERIAL_QUALITY}
        disabled
      />
      <MaterialSupportedComponentList
        name="buyer_material_component_values"
        materialId={materialId}
        materialColourId={materialColourId}
        category={MaterialComponentCategory.MATERIAL_QUALITY}
        title="Buyer material components"
        buttonText="Add buyer material component"
      />

      <Divider />
    </Box>
  );
};
